import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, Typography, Link } from '@material-ui/core';

import {
  ContentContainer, FlexContainer, HeaderBox, Title,
} from '@/pages/PreOnboarding/steps/ActivateSleepio/steps/AuthMethodSelection.styles';
import sleepioTheme from '@/themes/sleepioTheme';
import EmailAuthForm from '@/pages/Onboarding/components/EmailAuthForm/EmailAuthForm';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { logError } from '@/utils/logError';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { SLEEPIO_CANOPY_LOGIN_PATH, resetPasswordRedirectUrl } from '@/utils/passwordRoutes';

/**
 * "Set a password" — where a member who cannot get in asks for a link.
 *
 * The title is not "Reset your password", and that is deliberate. One screen serves
 * someone who has forgotten a password and someone setting their first, so it is
 * framed as a convenience rather than a security alert.
 *
 * **The confirmation is shown whatever happens**, including when the request fails.
 * Saying "we don't recognise that address" — or even failing differently — would tell
 * anyone who typed an address whether that person is a member, which is the same
 * disclosure the sign-in failure screen exists to prevent. The error is logged so we
 * can still see it; the member sees one message either way.
 */
const ForgotPasswordInner: React.FC = () => {
  useAcceptedLanguage();

  const { t } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();

  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Where to send the member's device once they have set a password, so the reset link
  // can carry it back. Validated before it is trusted.
  //
  // Looked for in two places, because no caller puts it in this screen's URL: every
  // navigation here comes from a button that passes the address in router state only
  // (`EmailAuthChoice`, `PasswordEntry`, and the expired-link retry in `ResetPassword`).
  // Reading the query alone therefore found nothing in practice, the reset email went
  // out with no deep link, and the member set a password in their default browser while
  // the app sat waiting — the exact failure `resetPasswordRedirectUrl` exists to stop.
  // `ActivationRouter` saves the value to session storage when the app first opens the
  // portal, so that copy is the fallback. Guarded: storage throws in a locked-down
  // browser, and losing the deep link must not take the screen down with it.
  const redirectTo = ((): string | null => {
    const fromUrl = new URLSearchParams(search).get('redirectTo');
    if (fromUrl && isSafeRedirectUri(fromUrl)) { return fromUrl; }

    try {
      const stored = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);
      if (stored && isSafeRedirectUri(stored)) { return stored; }
    } catch {
      // Unreadable storage reads as "no saved deep link", same as absent.
    }

    return null;
  })();

  // The screen that linked here passes the address in router state, and this screen
  // deliberately does not pre-fill from it. The form is `EmailAuthForm`, which is also
  // mounted on a route shared with other products, so it takes no initial-value prop
  // and adding one would change a component outside this work's remit. Asking the
  // member to type the address again is the cost of not reaching across that line.
  const handleSubmit = async (email: string) => {
    setLoading(true);
    try {
      // This call reports failure by *returning* an error, not by throwing, so the
      // catch below only ever sees a network fault. Without reading the returned value
      // the failures most likely to bite here vanish without trace: a rate limit, a
      // mail-provider fault, or the reset address not being on the project's allowed
      // list — which the platform handles by silently sending the member somewhere
      // else. The member still sees the same confirmation either way; this is only so
      // the failure is not invisible to us.
      const { error } = await getSupabaseClient().auth.resetPasswordForEmail(email, {
        redirectTo: resetPasswordRedirectUrl(redirectTo),
      });
      if (error) {
        logError('[ForgotPassword]', 'resetPasswordForEmail returned an error', error);
      }
    } catch (err) {
      logError('[ForgotPassword]', 'resetPasswordForEmail threw', err);
    } finally {
      // Always the same outcome, whatever the platform said.
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <ContentContainer>
      <SleepioHeader />
      <FlexContainer maxWidth="xs">
        {sent ? (
          <HeaderBox data-testid="reset-link-sent">
            <Title variant="h2" color="textPrimary">
              {t('coverage.pre_onboarding.sleepio.password.forgot.sent_title')}
            </Title>
            <Typography color="textPrimary">
              {t('coverage.pre_onboarding.sleepio.password.forgot.sent_body')}
            </Typography>
          </HeaderBox>
        ) : (
          <>
            <HeaderBox style={{ marginBottom: 24 }}>
              <Title variant="h2" color="textPrimary">
                {t('coverage.pre_onboarding.sleepio.password.forgot.title')}
              </Title>
              <Typography color="textPrimary">
                {t('coverage.pre_onboarding.sleepio.password.forgot.sub_title')}
              </Typography>
            </HeaderBox>

            <EmailAuthForm
              onSubmit={handleSubmit}
              loading={loading}
              showConsent={false}
              i18nKeys={{
                emailLabel: 'coverage.pre_onboarding.sleepio.password.forgot.email_label',
                continueButton: 'coverage.pre_onboarding.sleepio.password.forgot.submit',
                submittingButton: 'coverage.pre_onboarding.sleepio.password.forgot.submitting',
              }}
            />
          </>
        )}

        <Typography align="center" style={{ marginTop: 16 }}>
          <Link
            component="button"
            type="button"
            data-testid="back-to-login-link"
            // An explicit destination, not `goBack()`. A member who opened the reset
            // link from their email has no history to go back to, so the link would do
            // nothing; and going back from an already-spent link just lands on the
            // expired screen, which offers this same link — a loop with no way out.
            onClick={() => history.push(SLEEPIO_CANOPY_LOGIN_PATH)}
          >
            {t('coverage.pre_onboarding.sleepio.password.forgot.back')}
          </Link>
        </Typography>
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

const ForgotPassword: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <ForgotPasswordInner />
  </ThemeProvider>
);

export default ForgotPassword;
